import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks";
import { ICartItemProps } from "../../cartInterface";
import moment from "moment";
import * as S from "../styles.ts";
import { motion } from "framer-motion";
import { Button, LoadingSpinner } from "@/template/components/index.ts";
import { deleteCartItem } from "@/redux/slices/user/thunks/deleteCartItem.ts";
import { Trash2 } from "@/template/icons/index.ts";
import VoipUserCount from "../VoipUserCount/index.tsx";
import Help from "../Help/index.tsx";
import Extras from "../Extras/index.tsx";
import Tooltip from "@/template/components/Tooltip/index.tsx";
import { useAdvancedCartItemLogic } from "@/lib/helpers/hooks/useAdvancedCartItemLogic.ts";

const AddOnTooltip = ({ name, tooltipText }) => {
  const availableAddOns = ["Call Recording Retention"];

  if (!availableAddOns.includes(name)) return null;

  return <Tooltip>{tooltipText}</Tooltip>;
};

export const AdvancedCartItem = ({
  item,
  billing_interval,
  reviewOrder,
  isPaymentReceipt,
  setRenew,
  renew,
  orderDetail,
}: ICartItemProps) => {
  const dispatch = useTypedDispatch();

  const {
    checked,
    loading,
    shouldShowCostDifference,
    checkPrice,
    changeBillingCycle,
    tip,
    getItemLabel,
    currency,
    descriptions,
    user,
    shouldShowDomainPriceBreakdown,
    getDisplayPrice,
    getAddOnLabel,
    getRenewalDate,
    shouldDisable,
    cartReady,
    checkDateRange,
    checkBoxChecked,
    handleChange,
    shouldShow,
  } = useAdvancedCartItemLogic({
    item,
    billing_interval,
    reviewOrder,
    setRenew,
    renew,
    orderDetail,
  });

  if (!cartReady) return null;

  const validTypes = ["VoIP Plan Change", "User", "VoIP Addon"];

  return (
    <S.CartItemWrapper
      as={motion.div}
      animate={{
        height: "auto",
        opacity: 1,
      }}
      exit={{
        height: 0,
        opacity: 0,
      }}
      $advanced
      $isPlan={validTypes.includes(item?.type?.name)}
    >
      <S.CartItemContainer tabIndex={0}>
        <S.ItemType>
          <S.LeftContent>
            {!reviewOrder ? (
              <Button
                size="xs"
                variant="light"
                isIcon
                onClick={(e) => {
                  e.stopPropagation();
                  if (loading) return;
                  dispatch(deleteCartItem(item.id));
                }}
              >
                <Trash2 fontSize={16} />
              </Button>
            ) : null}
            {getItemLabel()}
            <AddOnTooltip
              name={item?.name}
              tooltipText="Record, store and playback your last 12 months of calls."
            />
            <Help tip={tip} />
          </S.LeftContent>
          <S.RightRow>
            <S.CenterContent>
              {(item.type.name === "User" ||
                item?.type?.name === "VoIP Plan Change") && (
                <VoipUserCount
                  item={item}
                  isPaymentReceipt={isPaymentReceipt}
                  reviewOrder={reviewOrder}
                />
              )}
            </S.CenterContent>
            <S.RightContent>
              {shouldShowDomainPriceBreakdown && (
                <S.CartItemPriceStrike>
                  {currency}
                  {(item.cost_recurring / 100).toFixed(2)}
                </S.CartItemPriceStrike>
              )}
              <S.CartItemPrice>
                {loading ? (
                  <LoadingSpinner width={20} />
                ) : (
                  <>{getDisplayPrice()}</>
                )}
              </S.CartItemPrice>
            </S.RightContent>
          </S.RightRow>
        </S.ItemType>
        <Extras
          item={item}
          reviewOrder={reviewOrder}
        />
        {(item?.type?.name === "User" ||
          item?.type?.name === "VoIP Plan Change") && (
          <S.PlanContainer>
            <S.PlanSection>
              <S.PlanLabel>Billing cycle</S.PlanLabel>
              <S.PlanRadioGroup>
                <S.PlanRadioLabel>
                  <S.PlanRadio
                    type="radio"
                    name="yearly"
                    disabled={shouldDisable}
                    onChange={() => changeBillingCycle("yearly")}
                    checked={checked === "yearly"}
                  />
                  <span>Yearly </span>
                  <Tooltip>
                    The yearly plan is paid per user and in full at checkout.
                  </Tooltip>{" "}
                  {checkPrice("yearly")} per user / month, paid yearly
                </S.PlanRadioLabel>
                <S.PlanRadioLabel>
                  <S.PlanRadio
                    type="radio"
                    name="monthly"
                    disabled={shouldDisable || checkDateRange()}
                    onChange={() => changeBillingCycle("monthly")}
                    checked={checked === "monthly"}
                  />
                  <span>Monthly</span> {checkPrice("monthly")} per user / month
                </S.PlanRadioLabel>
              </S.PlanRadioGroup>
            </S.PlanSection>

            <S.PlanSection>
              <S.PlanLabel>Select Add-Ons</S.PlanLabel>
              <S.PlanCheckboxGroup>
                <S.PlanCheckbox
                  type="checkbox"
                  disabled={shouldShow}
                  checked={checkBoxChecked}
                  onChange={handleChange} // Need better way
                />
                <span>12 Month Call Recording Storage</span>
                <Tooltip>
                  Record, store and playback your last 12 months of calls.
                </Tooltip>
                <p>{getAddOnLabel()}</p>
              </S.PlanCheckboxGroup>
            </S.PlanSection>

            <S.PlanRenewText>
              <span>Renews: </span>
              <S.PlanHighlightDate>{getRenewalDate()}</S.PlanHighlightDate>
              <Tooltip>Your plan will auto-renew on this date.</Tooltip>
            </S.PlanRenewText>
          </S.PlanContainer>
        )}

        {item?.type?.name === "VoIP Addon" && (
          <S.PlanContainer>
            <S.PlanRenewText style={{ marginTop: 0 }}>
              <span>Renews: </span>
              <S.PlanHighlightDate>
                {moment(user?.renewal_date).format("Do MMM YYYY")}
              </S.PlanHighlightDate>
              <Tooltip>
                Your Add-On will auto-renew with your plan on this date.
              </Tooltip>
            </S.PlanRenewText>
          </S.PlanContainer>
        )}
        <S.InlineContainer>
          {descriptions.map((d, i) => (
            <S.CartItemLabel key={i}>{d}</S.CartItemLabel>
          ))}

          {shouldShowCostDifference && (
            <S.CartItemLabel>
              <span>
                Promo:{" "}
                {Math.round(
                  ((item.cost_recurring - item.cost_net) /
                    item.cost_recurring) *
                    100
                )}
                % Off
              </span>
            </S.CartItemLabel>
          )}
        </S.InlineContainer>
      </S.CartItemContainer>
    </S.CartItemWrapper>
  );
};

export default AdvancedCartItem;
